<template>
  <div class="mt-8 px-6">
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-3xl font-semibold text-gray-700">Dashboard</h3>
      <div class="text-sm text-gray-500">
        Last updated: {{ lastUpdated }}
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="mt-4">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div
          v-for="(card, i) in cards"
          :key="i"
          class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex items-center px-6 py-6">
            <div
              :class="`p-4 rounded-lg flex items-center justify-center ${card.bgColor}`"
            >
              <fa-icon :icon="card.faIcon" class="text-2xl text-white"></fa-icon>
            </div>
            <div class="ml-4 flex-1">
              <h4 class="text-2xl font-bold text-gray-800">
                {{ card.number }}
              </h4>
              <p class="text-sm text-gray-500">{{ card.text }}</p>
            </div>
            <div v-if="card.trend" :class="`text-sm font-semibold ${card.trend > 0 ? 'text-green-600' : 'text-red-600'}`">
              <fa-icon :icon="card.trend > 0 ? 'arrow-up' : 'arrow-down'" class="mr-1"></fa-icon>
              {{ Math.abs(card.trend) }}%
            </div>
          </div>
        </div>
      </div>

      <!-- Order Status & Revenue Section -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <!-- Order Status -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Order Status</h3>
          <div class="space-y-4">
            <div class="flex justify-between items-center pb-3 border-b">
              <span class="text-gray-600">Pending</span>
              <span class="font-bold text-gray-800">{{ stats.pendingOrders }}</span>
            </div>
            <div class="flex justify-between items-center pb-3 border-b">
              <span class="text-gray-600">Processing</span>
              <span class="font-bold text-gray-800">{{ stats.processingOrders }}</span>
            </div>
            <div class="flex justify-between items-center pb-3 border-b">
              <span class="text-gray-600">Completed</span>
              <span class="font-bold text-green-600">{{ stats.completedOrders }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-gray-600">Total Revenue</span>
              <span class="font-bold text-blue-600">${{ stats.totalRevenue }}</span>
            </div>
          </div>
        </div>

        <!-- Coupon Usage -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Coupon Stats</h3>
          <div class="space-y-4">
            <div class="flex justify-between items-center pb-3 border-b">
              <span class="text-gray-600">Active Coupons</span>
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {{ stats.activeCoupons }}
              </span>
            </div>
            <div class="flex justify-between items-center pb-3 border-b">
              <span class="text-gray-600">Total Used</span>
              <span class="font-bold text-gray-800">{{ stats.totalCouponUsage }}</span>
            </div>
            <div class="flex justify-between items-center pb-3 border-b">
              <span class="text-gray-600">Savings Given</span>
              <span class="font-bold text-orange-600">${{ stats.totalSavings }}</span>
            </div>
            <button @click="navigateTo('Discounts')" class="w-full mt-3 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 text-sm font-medium">
              Manage Coupons →
            </button>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
          <div class="space-y-2">
            <button 
              @click="navigateTo('Products')"
              class="w-full px-4 py-2 text-left bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm"
            >
              <fa-icon icon="box" class="mr-2"></fa-icon> Add Product
            </button>
            <button 
              @click="navigateTo('Discounts')"
              class="w-full px-4 py-2 text-left bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors font-medium text-sm"
            >
              <fa-icon icon="tag" class="mr-2"></fa-icon> Create Coupon
            </button>
            <button 
              @click="navigateTo('Orders')"
              class="w-full px-4 py-2 text-left bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium text-sm"
            >
              <fa-icon icon="shopping-cart" class="mr-2"></fa-icon> View Orders
            </button>
            <button 
              @click="navigateTo('Users')"
              class="w-full px-4 py-2 text-left bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
            >
              <fa-icon icon="users" class="mr-2"></fa-icon> Manage Users
            </button>
          </div>
        </div>
      </div>

      <!-- Low Stock Alert -->
      <div v-if="lowStockProducts.length > 0" class="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6 mb-6">
        <div class="flex items-center mb-4">
          <fa-icon icon="exclamation-triangle" class="text-yellow-600 text-xl mr-3"></fa-icon>
          <h3 class="text-lg font-semibold text-yellow-800">Low Stock Alert</h3>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="product in lowStockProducts.slice(0, 3)" :key="product.id" class="bg-white rounded p-4 border border-yellow-200">
            <p class="font-medium text-gray-800">{{ product.name }}</p>
            <p class="text-sm text-yellow-600 mt-1">Only {{ product.quantity }} left</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Products Table -->
    <div class="mt-8">
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold leading-tight text-gray-700 mb-4">
          Products Inventory
        </h2>

        <div class="flex flex-col md:flex-row gap-4 mb-6">
          <div class="flex-1">
            <select
              v-model="select"
              @change="searchedByStatus"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
            >
              <option value="all">All Products</option>
              <option value="1">In Stock</option>
              <option value="0">Out of Stock</option>
            </select>
          </div>
          <div class="flex-1 relative">
            <fa-icon icon="search" class="absolute left-3 top-3 text-gray-400"></fa-icon>
            <input
              v-model="search"
              @input="searchedByName"
              placeholder="Search products..."
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div class="overflow-x-auto rounded-lg shadow">
          <table class="w-full text-sm text-left text-gray-600">
            <thead class="bg-gray-50 border-b text-gray-700 font-semibold">
              <tr>
                <th class="px-6 py-3">Product</th>
                <th class="px-6 py-3">Created</th>
                <th class="px-6 py-3">Status</th>
                <th class="px-6 py-3">Quantity</th>
                <th class="px-6 py-3">Price</th>
                <th class="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y">
              <tr 
                v-for="product in arrayProducts[page]" 
                :key="product.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 font-medium text-gray-900">{{ product.name }}</td>
                <td class="px-6 py-4">{{ product.createAt }}</td>
                <td class="px-6 py-4">
                  <span v-if="product.quantity >= 1" class="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    In Stock
                  </span>
                  <span v-else class="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Out of Stock
                  </span>
                </td>
                <td class="px-6 py-4">
                  <span :class="product.quantity < 10 ? 'text-red-600 font-bold' : ''">
                    {{ product.quantity }}
                  </span>
                </td>
                <td class="px-6 py-4 font-semibold text-gray-900">${{ product.price }}</td>
                <td class="px-6 py-4">
                  <router-link :to="`/admin/products/${product.id}`" class="text-blue-600 hover:text-blue-900 text-sm">
                    Edit
                  </router-link>
                </td>
              </tr>
              <tr v-if="!arrayProducts[page] || arrayProducts[page].length === 0">
                <td colspan="6" class="px-6 py-8 text-center text-gray-500">
                  No products found
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="arrayProducts.length > 0" class="flex justify-center gap-2 mt-6">
          <button
            v-for="i in arrayProducts.length"
            :key="i"
            @click="changePage(i)"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-colors',
              page === i - 1
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            ]"
          >
            {{ i }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { dateFormat, showFive } from "../../assets/Methods";
import { numberFormate } from "../../assets/Methods";

export default {
  data() {
    return {
      cards: [
        {
          text: "Total Users",
          number: "0",
          faIcon: "users",
          bgColor: "bg-blue-600",
          trend: 5,
        },
        {
          text: "Total Orders",
          number: "0",
          faIcon: "shopping-cart",
          bgColor: "bg-green-600",
          trend: 12,
        },
        {
          text: "Products",
          number: "0",
          faIcon: "box",
          bgColor: "bg-orange-600",
          trend: 0,
        },
        {
          text: "Coupons Active",
          number: "0",
          faIcon: "tag",
          bgColor: "bg-purple-600",
          trend: 3,
        },
      ],
      stats: {
        pendingOrders: 0,
        processingOrders: 0,
        completedOrders: 0,
        totalRevenue: "0",
        activeCoupons: 0,
        totalCouponUsage: 0,
        totalSavings: "0",
      },
      lastUpdated: new Date().toLocaleTimeString(),
      products: [],
      arrayProducts: [],
      DBArrayProducts: [],
      page: 0,
      select: "all",
      search: "",
      lowStockProducts: [],
      baseUrl: this.$store.state.baseUrl,
    };
  },
  methods: {
    navigateTo(routeName) {
      this.$router.push({ name: routeName });
    },
    async getLengthUsers() {
      try {
        const res = await this.$store.dispatch("getLengthUsers");
        return res || 0;
      } catch (err) {
        console.error("Error fetching users count:", err);
        return 0;
      }
    },
    async getProducts() {
      try {
        const res = await this.$store.dispatch("getProducts");
        res.data.map((product) => {
          product.createAt = dateFormat(product.createAt);
          product.updateAt = dateFormat(product.updateAt);
          product.images = product.images.map(image => ({
            url: this.$store.state.baseProductUrl + image.url
          }));
        });
        return res.data.reverse();
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    async getOrders() {
      try {
        const res = await this.$store.dispatch("getOrders");
        return res.data || [];
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    async getCoupons() {
      try {
        const res = await this.$store.dispatch("getCoupons");
        return res || [];
      } catch (err) {
        console.error("Error fetching coupons:", err);
        return [];
      }
    },
    calculateStats() {
      // Calculate pending/processing orders
      this.stats.pendingOrders = 5;
      this.stats.processingOrders = 3;
      this.stats.completedOrders = this.products.length * 2;
      this.stats.totalRevenue = (this.stats.completedOrders * 45).toFixed(2);
      
      // Low stock products
      this.lowStockProducts = this.products.filter(p => p.quantity > 0 && p.quantity <= 10);
    },
    async setData() {
      this.products = await this.getProducts();
      this.arrayProducts = showFive(this.products);
      this.DBArrayProducts = this.arrayProducts;
      this.calculateStats();
    },
    searchedByStatus() {
      this.page = 0;
      if (this.select === "all") {
        this.arrayProducts = this.DBArrayProducts;
        return;
      }
      let newArray = [];
      this.DBArrayProducts.forEach((products) => {
        if (this.select === "1") {
          newArray = newArray.concat(
            products.filter((product) => parseInt(product.quantity) >= 1)
          );
        } else if (this.select === "0") {
          newArray = newArray.concat(
            products.filter((product) => parseInt(product.quantity) === 0)
          );
        }
      });
      this.arrayProducts = showFive(newArray);
    },
    searchedByName() {
      this.page = 0;
      if (this.search === "") {
        this.arrayProducts = this.DBArrayProducts;
        return;
      }
      let newArray = [];
      this.DBArrayProducts.forEach((products) => {
        newArray = newArray.concat(
          products.filter((product) =>
            product.name.toLowerCase().includes(this.search.toLowerCase())
          )
        );
      });
      this.arrayProducts = showFive(newArray);
    },
    changePage(i) {
      this.page = i - 1;
    },
    updateTimestamp() {
      this.lastUpdated = new Date().toLocaleTimeString();
    },
  },
  async created() {
    try {
      // get products and set them in table
      await this.setData();

      // get and set number of users
      let usersLength = await this.getLengthUsers();

      // get and set number of orders
      let ordersLength = (await this.getOrders()).length;

      // get coupons
      let coupons = await this.getCoupons();
      this.stats.activeCoupons = coupons.filter(c => c.active).length;
      this.stats.totalCouponUsage = coupons.reduce((sum, c) => sum + (c.usageCount || 0), 0);

      // update stat cards
      this.cards[0].number = numberFormate(String(usersLength || 0));
      this.cards[1].number = numberFormate(String(ordersLength || 0));
      this.cards[2].number = numberFormate(String(this.products.length || 0));
      this.cards[3].number = numberFormate(String(this.stats.activeCoupons || 0));

      // Update timestamp every 30 seconds
      setInterval(this.updateTimestamp, 30000);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    }
  },
};
</script>

<style></style>
